import { control, curve, display, readout, type PhysicsDomain } from './domainTypes';

/** SI defining constants, exact since the 2019 SI revision. */
export const PLANCK = 6.62607015e-34;
export const LIGHT_SPEED = 299792458;
export const ELEMENTARY_CHARGE = 1.602176634e-19;
const HYDROGEN_ENERGY_EV = 13.6;

export const modernPhysics: PhysicsDomain = {
  id: 'modern-physics',
  name: 'Modern Physics Lab',
  hint: 'Test light thresholds, discrete atomic transitions and relativistic clock comparisons.',
  experiments: [
    {
      id: 'photoelectric',
      name: 'Photoelectric threshold',
      question: 'Can brighter light eject electrons when each photon has too little energy?',
      controls: [
        control('wavelength', 'Light wavelength', 'nm', 200, 800, 400, 10),
        control('work', 'Metal work function', 'eV', 1, 6, 2.3, 0.1),
        control('intensity', 'Relative light intensity', '', 0, 5, 1, 0.1),
      ],
      examples: [
        {
          id: 'violet',
          name: 'Above threshold energy',
          values: {},
          note: 'Shorter wavelengths supply more energy per photon.',
        },
        {
          id: 'red-bright',
          name: 'Bright but below threshold',
          values: { wavelength: 700, intensity: 5 },
          note: 'Increasing intensity cannot overcome the energy threshold in this model.',
        },
        {
          id: 'violet-bright',
          name: 'Brighter violet',
          values: { intensity: 5 },
          note: 'More photons can eject more electrons, but maximum electron energy stays fixed.',
        },
      ],
      assumptions:
        'Ideal single-photon photoelectric model with no space-charge or multiphoton effects. Intensity changes photon supply, not energy per photon. The relative emission indicator is not a calibrated current. Equality is the ideal zero-kinetic-energy threshold.',
      source:
        'https://openstax.org/books/university-physics-volume-3/pages/6-2-photoelectric-effect',
      calculate: ({ wavelength, work, intensity }) => {
        const energy = (PLANCK * LIGHT_SPEED) / (wavelength * 1e-9) / ELEMENTARY_CHARGE;
        const kinetic = Math.max(0, energy - work);
        const emitted = energy >= work && intensity > 0;
        return {
          readouts: [
            readout('Energy per photon', energy, 'eV'),
            readout('Maximum electron KE if illuminated', kinetic, 'eV'),
            readout(
              'Threshold wavelength',
              ((PLANCK * LIGHT_SPEED) / (work * ELEMENTARY_CHARGE)) * 1e9,
              'nm',
            ),
            readout('Relative emission indicator', emitted ? intensity : 0, ''),
          ],
          summary:
            intensity === 0
              ? 'The light is off; no electrons are emitted.'
              : emitted
                ? 'Photons reach the threshold. Brightness changes supply, not maximum electron energy.'
                : 'Each photon has too little energy. More brightness still does not eject electrons.',
          working: [
            'Ephoton = h c / λ',
            `Ephoton = ${display(energy)} eV`,
            'Kmax = max(0, Ephoton − φ)',
            `Kmax = max(0, ${display(energy)} − ${work}) = ${display(kinetic)} eV`,
          ],
          plot: curve(10, (frequency) =>
            Math.max(0, (PLANCK * frequency * 1e14) / ELEMENTARY_CHARGE - work),
          ),
          xLabel: 'Frequency (10¹⁴ Hz)',
          yLabel: 'Maximum electron KE (eV)',
        };
      },
      prediction: {
        question: 'Light is below the frequency threshold. Increase its intensity. What happens?',
        options: ['No electrons are ejected', 'Electrons become faster', 'Threshold energy falls'],
        answer: 0,
        why: 'Each photon must supply at least the work function. More low-energy photons do not change that threshold.',
      },
      task: 'Calculate the threshold wavelength for a 3 eV work function. Test wavelengths on either side, then change intensity.',
    },
    {
      id: 'spectrum',
      name: 'Hydrogen emission',
      question: 'Why does an atom emit specific wavelengths rather than every colour?',
      controls: [
        control('upper', 'Initial upper level', '', 2, 6, 3),
        control('lower', 'Final lower level', '', 1, 5, 2),
      ],
      examples: [
        {
          id: 'red-line',
          name: 'Level 3 to 2',
          values: {},
          note: 'The model gives a visible red emission line.',
        },
        {
          id: 'blue-line',
          name: 'Level 4 to 2',
          values: { upper: 4 },
          note: 'A larger energy gap emits a shorter wavelength.',
        },
        {
          id: 'uv-line',
          name: 'Level 2 to 1',
          values: { upper: 2, lower: 1 },
          note: 'This transition produces ultraviolet light, outside visible colours.',
        },
      ],
      assumptions:
        'Rounded Bohr hydrogen energies: En = −13.6/n² eV. No fine structure, line width or multi-electron atoms. Only integer downward transitions are allowed. The energy diagram is schematic.',
      source:
        'https://openstax.org/books/university-physics-volume-3/pages/6-4-bohrs-model-of-the-hydrogen-atom',
      calculate: ({ upper, lower }) => {
        const valid = Number.isInteger(upper) && Number.isInteger(lower) && upper > lower;
        const energy = valid ? HYDROGEN_ENERGY_EV * (1 / lower ** 2 - 1 / upper ** 2) : 0;
        const wavelength = valid
          ? ((PLANCK * LIGHT_SPEED) / (energy * ELEMENTARY_CHARGE)) * 1e9
          : 0;
        return {
          readouts: valid
            ? [
                readout('Photon energy', energy, 'eV'),
                readout('Emission wavelength', wavelength, 'nm'),
              ]
            : [],
          summary: valid
            ? `A discrete energy gap produces a ${display(wavelength)} nm photon.`
            : 'Choose whole-number levels with the initial level higher than the final level. No emission transition is calculated.',
          working: valid
            ? [
                'En = −13.6 / n² eV',
                `ΔE = 13.6(1/${lower}² − 1/${upper}²) = ${display(energy)} eV`,
                `λ = h c / ΔE = ${display(wavelength)} nm`,
              ]
            : ['A downward emission transition requires integer levels and ninitial > nfinal.'],
          plot: Array.from({ length: 6 }, (_, index) => ({
            x: index + 1,
            y: -HYDROGEN_ENERGY_EV / (index + 1) ** 2,
          })),
          xLabel: 'Hydrogen level n',
          yLabel: 'Level energy (eV)',
        };
      },
      prediction: {
        question:
          'A downward transition has a larger energy gap. What happens to photon wavelength?',
        options: ['It becomes shorter', 'It becomes longer', 'It stays the same'],
        answer: 0,
        why: 'Photon energy and wavelength are inversely related: E = h c / λ.',
      },
      task: 'Compare transitions 3 → 2 and 4 → 2. Predict which has the shorter wavelength before checking.',
    },
    {
      id: 'relativity',
      name: 'Moving clocks and lengths',
      question:
        'Which elapsed time does the laboratory assign between two ticks of a moving clock?',
      controls: [
        control('beta', 'Relative speed as fraction of c', '', 0, 0.99, 0.6, 0.01),
        control('proper', 'Moving clock proper interval', 's', 1, 20, 5),
        control('length', 'Object rest length', 'm', 1, 100, 10),
      ],
      examples: [
        {
          id: 'moderate',
          name: 'At 0.6c',
          values: {},
          note: 'The laboratory interval is longer than the interval measured by the moving clock.',
        },
        {
          id: 'rest',
          name: 'At rest',
          values: { beta: 0 },
          note: 'At zero relative speed, intervals and lengths agree.',
        },
        {
          id: 'fast',
          name: 'At 0.9c',
          values: { beta: 0.9 },
          note: 'Relativistic effects grow as relative speed approaches c.',
        },
      ],
      assumptions:
        'Two inertial frames at constant relative speed. Proper time is measured between ticks at one location in the clock frame. Laboratory length is measured simultaneously in the laboratory, along the motion direction. This is not a twin-paradox trip.',
      source: 'https://openstax.org/books/university-physics-volume-3/pages/5-3-time-dilation',
      calculate: ({ beta, proper, length }) => {
        const gamma = 1 / Math.sqrt(1 - beta ** 2);
        return {
          readouts: [
            readout('Lorentz factor', gamma, ''),
            readout('Clock proper interval', proper, 's'),
            readout('Laboratory interval', gamma * proper, 's'),
            readout('Laboratory length', length / gamma, 'm'),
          ],
          summary:
            beta === 0
              ? 'At rest relative to the laboratory, both intervals and lengths agree.'
              : 'The laboratory assigns a longer interval between moving-clock ticks and a shorter length along motion.',
          working: [
            'γ = 1 / √(1 − v²/c²)',
            `γ = 1 / √(1 − ${beta}²) = ${display(gamma)}`,
            `Δtlab = γ Δτ = ${display(gamma * proper)} s`,
            `Llab = Lrest / γ = ${display(length / gamma)} m`,
          ],
          plot: curve(0.99, (b) => 1 / Math.sqrt(1 - b ** 2)),
          xLabel: 'Speed / c',
          yLabel: 'Lorentz factor',
        };
      },
      prediction: {
        question:
          'For ticks at one position in the clock frame, what does the laboratory measure when the clock moves?',
        options: [
          'A longer interval between ticks',
          'A shorter proper interval',
          'A change in light speed',
        ],
        answer: 0,
        why: 'Time dilation relates the laboratory interval to proper time: Δtlab = γ Δτ.',
      },
      task: 'Predict the laboratory interval for 4 s of proper time at 0.8c. Test it and name both reference frames.',
    },
  ],
};
